import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { GoogleButton, Field } from "@/pages/Login";
import { PasswordChecklist, passwordValid } from "@/components/PasswordChecklist";
import { ConsentFields, EMPTY_CONSENTS, consentsComplete, stashConsents } from "@/components/ConsentFields";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { loginEnabled, loaded } = useSettings();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState(EMPTY_CONSENTS);
  const [consentErr, setConsentErr] = useState(false);
  const checkConsents = () => {
    if (consentsComplete(consents)) return true;
    setConsentErr(true);
    toast.error("Seleziona Acconsento o Non acconsento per entrambi i consensi");
    return false;
  };
  const beforeGoogle = () => { if (!checkConsents()) return false; stashConsents(consents); };

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { toast.error("Inserisci nome e cognome"); return; }
    if (!passwordValid(password)) { toast.error("La password non rispetta i requisiti indicati"); return; }
    if (!checkConsents()) return;
    setLoading(true);
    try {
      await register(firstName.trim(), lastName.trim(), email, password, consents);
      toast.success("Account creato, benvenuto su Readoo!");
      navigate("/benvenuto");
    } catch (err) {
      toast.error(err.message || "Registrazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  if (loaded && !loginEnabled) {
    return (
      <div className="max-w-[440px] mx-auto px-4 sm:px-6 py-16 text-center" data-testid="register-closed">
        <div className="inline-flex items-baseline gap-0.5 mb-4">
          <span className="font-display text-3xl font-bold text-onSurface">Readoo</span>
          <span className="w-2 h-2 rounded-full bg-brand mb-1.5" />
        </div>
        <h1 className="font-serif text-2xl font-semibold text-onSurface">Registrazioni chiuse</h1>
        <p className="text-sm text-subtle mt-2">Le nuove registrazioni non sono al momento disponibili. Torna a leggere le notizie.</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">
          Vai alla home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[440px] mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-baseline gap-0.5 mb-3">
          <span className="font-display text-3xl font-bold text-onSurface">Readoo</span>
          <span className="w-2 h-2 rounded-full bg-brand mb-1.5" />
        </div>
        <h1 className="font-serif text-2xl font-semibold text-onSurface">Crea il tuo account gratis</h1>
        <p data-testid="register-tagline" className="text-sm text-subtle mt-1">Ti informa. Ti coinvolge. Ti premia.</p>
      </div>

      <div className="bg-surfaceSecondary border border-hair rounded-2xl p-6 sm:p-8">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field icon={User} type="text" placeholder="Nome" value={firstName} onChange={setFirstName} testid="register-first-name" autoComplete="given-name" />
            <Field icon={User} type="text" placeholder="Cognome" value={lastName} onChange={setLastName} testid="register-last-name" autoComplete="family-name" />
          </div>
          <Field icon={Mail} type="email" placeholder="La tua email" value={email} onChange={setEmail} testid="register-email" />
          <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="register-password" autoComplete="new-password" />
          <PasswordChecklist value={password} testid="register-password-rules" />
          <ConsentFields value={consents} onChange={(v) => { setConsents(v); setConsentErr(false); }} error={consentErr} idPrefix="register-consent" />
          <button type="submit" disabled={loading || !passwordValid(password)} data-testid="register-submit-button" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-brand text-onBrand font-semibold text-sm hover:bg-brandSecondary disabled:opacity-60 transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Registrati"}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-hair" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-subtle">oppure</span>
          <div className="flex-1 h-px bg-hair" />
        </div>
        <GoogleButton beforeStart={beforeGoogle} />
      </div>

      <p className="text-center text-sm text-subtle mt-6">
        Hai già un account? <Link to="/login" data-testid="go-login" className="text-brand font-semibold hover:underline">Accedi</Link>
      </p>
    </div>
  );
}
